import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ThemeProvider } from "next-themes";
import { THEME_STORAGE_KEY } from "@/lib/theme";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { ProfileSwitchProvider } from "@/contexts/ProfileSwitchContext";
import { BrandProvider } from "@/contexts/BrandContext";
import { CreditsProvider } from "@/contexts/CreditsContext";
import { FeatureFlagsProvider } from "@/hooks/useFeatureFlags";
import { AnimatedRoutes } from "@/components/AnimatedRoutes";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { useSessionLifecycle } from "@/hooks/useSessionLifecycle";

/**
 * QueryClient configured with enterprise best practices:
 * 
 * - refetchOnWindowFocus: FALSE  -  prevents all queries from re-firing when the user
 *   switches back to the tab. This was causing the "refresh on tab switch" behavior.
 *   Enterprise apps (Notion, Slack, Linear) all disable this.
 * 
 * - staleTime: 5 minutes  -  data is considered fresh for 5 minutes, reducing unnecessary
 *   network requests. After 5 minutes, the next component mount will refetch.
 * 
 * - gcTime (formerly cacheTime): 30 minutes  -  keeps inactive query data in memory
 *   for 30 minutes before garbage collecting. This means if the user navigates away
 *   and comes back within 30 min, they see cached data instantly.
 * 
 * - refetchOnReconnect: TRUE  -  when the user goes offline and comes back online,
 *   stale data is refreshed. This is the RIGHT time to refetch.
 * 
 * - retry: 1  -  retry failed requests once (not 3 times which is the default).
 *   Reduces perceived lag on actual errors.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,       // 5 minutes
      gcTime: 30 * 60 * 1000,          // 30 minutes
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

function AppInner() {
  useVersionCheck();
  useSessionLifecycle();

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AnimatedRoutes />
      </BrowserRouter>
    </TooltipProvider>
  );
}

const App = () => (
  <ErrorBoundary>
    {/* Dark is strictly opt-in: defaultTheme light, enableSystem off.
        The dark palette exists in index.css but no screen has ever been reviewed in it, so
        letting `system` decide would silently flip every member on a dark OS into an
        unreviewed theme on their next load. They can turn it on from the profile menu.
        disableTransitionOnChange stops every colour-transitioning element on the page from
        animating at once when the class flips, which reads as a lag rather than a switch. */}
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem={false}
      storageKey={THEME_STORAGE_KEY}
      disableTransitionOnChange
    >
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ProfileSwitchProvider>
        <ImpersonationProvider>
          <BrandProvider>
            <CreditsProvider>
            <FeatureFlagsProvider>
              <AppInner />
            </FeatureFlagsProvider>
            </CreditsProvider>
          </BrandProvider>
        </ImpersonationProvider>
        </ProfileSwitchProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
